import { IconChartLine, IconMail, IconSchool, IconShoppingBag, IconUserPlus, IconUsersGroup } from "@tabler/icons-react"
import { type ComponentType, type FC, type ReactNode } from "react"

import {
	type AnalyticsProductType,
	type BusinessAnalytics,
	type CheckoutAnalytics,
} from "@forento/core/models/analytics"
import dayjs from "@forento/core/utilities/dayjs"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import { AnalyticsCard, AnalyticsCardHeader } from "./shared"

const BusinessInsights: FC<{ data: BusinessAnalytics }> = ({ data }) => {
	const courseLearners = data.courseCompletions.reduce((sum, course) => sum + course.learners, 0)
	const courseCompletions = data.courseCompletions.reduce((sum, course) => sum + course.completed, 0)
	const courseCompletionRate = toRate(courseCompletions, courseLearners)
	const topProduct = data.productConversions[0] ?? null

	return (
		<div className="mt-5 flex flex-col gap-5">
			<div className="flex flex-wrap gap-5">
				<MetricCard
					Icon={IconChartLine}
					label="Product conversion"
					value={topProduct === null ? "-" : formatPercent(topProduct.conversionRate)}
					detail={
						topProduct === null ? "No purchases" : `${topProduct.product.name}: ${topProduct.purchases}`
					}
				/>
				<MetricCard
					Icon={IconUserPlus}
					label="Signup purchase"
					value={formatPercent(data.signupFunnel.signupToPurchaseRate)}
					detail={`${toNumberFormat(data.signupFunnel.buyers)} buyers from ${toNumberFormat(data.signupFunnel.signups)}`}
				/>
				{data.checkout.status === "tracked" && (
					<MetricCard
						Icon={IconShoppingBag}
						label="Abandoned checkout"
						value={formatPercent(data.checkout.abandonmentRate)}
						detail={getCheckoutSummary(data.checkout)}
					/>
				)}
				<MetricCard
					Icon={IconSchool}
					label="Completion rate"
					value={formatPercent(courseCompletionRate)}
					detail={`${toNumberFormat(courseCompletions)} completions from ${toNumberFormat(courseLearners)}`}
				/>
				<MetricCard
					Icon={IconUsersGroup}
					label="Community engagement"
					value={formatPercent(data.communityEngagement.engagementRate)}
					detail={`${toNumberFormat(data.communityEngagement.activeProfiles)} active profiles`}
				/>
				<MetricCard
					Icon={IconMail}
					label="Email delivery"
					value={formatPercent(data.emailPerformance.deliveryRate)}
					detail={`${toNumberFormat(data.emailPerformance.sent)} sent, ${toNumberFormat(data.emailPerformance.failed)} failed`}
				/>
			</div>

			<div className="flex flex-wrap gap-5">
				<DetailTable
					title="Product conversion"
					className="flex-[1.3_1_520px]"
					columns={[
						{ label: "Product", className: "w-1/2" },
						{ label: "Purchases", align: "right" },
						{ label: "Conversion", align: "right" },
					]}
					data={data.productConversions.map(product => [
						<ProductName key={product.product.id} product={product.product} />,
						toNumberFormat(product.purchases),
						formatPercent(product.conversionRate),
					])}
					emptyText="No purchases"
				/>
				<FunnelCard data={data.signupFunnel} />
			</div>

			<div className="flex flex-wrap gap-5">
				<DetailTable
					title="Cohort retention"
					className="flex-[1_1_420px]"
					columns={[
						{ label: "Cohort", className: "w-2/5" },
						{ label: "Users", align: "right" },
						{ label: "Active", align: "right" },
						{ label: "Retention", align: "right" },
					]}
					data={data.cohortRetention.map(cohort => [
						dayjs(`${cohort.cohort}-01`).format("MMM YYYY"),
						toNumberFormat(cohort.users),
						toNumberFormat(cohort.retainedUsers),
						formatPercent(cohort.retentionRate),
					])}
					emptyText="No cohorts"
				/>
				<DetailTable
					title="Completion rate"
					className="flex-[1.2_1_500px]"
					columns={[
						{ label: "Course", className: "w-1/2" },
						{ label: "Learners", align: "right" },
						{ label: "Completed", align: "right" },
						{ label: "Rate", align: "right" },
					]}
					data={data.courseCompletions.map(course => [
						<CourseName key={course.courseId} title={course.courseTitle} pageCount={course.pageCount} />,
						toNumberFormat(course.learners),
						toNumberFormat(course.completed),
						formatPercent(course.completionRate),
					])}
					emptyText="No course activity"
				/>
			</div>

			<div className="flex flex-wrap gap-5">
				{data.checkout.status === "tracked" && (
					<DetailTable
						title="Checkout"
						className="flex-[1_1_280px]"
						columns={[{ label: "Metric" }, { label: "Value", align: "right" }]}
						data={getCheckoutRows(data.checkout)}
					/>
				)}
				<DetailTable
					title="Community"
					className="flex-[1_1_320px]"
					columns={[{ label: "Metric" }, { label: "Value", align: "right" }]}
					data={[
						["Profiles", toNumberFormat(data.communityEngagement.profiles)],
						["Active profiles", toNumberFormat(data.communityEngagement.activeProfiles)],
						["Posts", toNumberFormat(data.communityEngagement.posts)],
						["Comments", toNumberFormat(data.communityEngagement.comments)],
						["Reactions", toNumberFormat(data.communityEngagement.reactions)],
						["Messages", toNumberFormat(data.communityEngagement.messages)],
					]}
				/>
				<DetailTable
					title="Email performance"
					className="flex-[1_1_360px]"
					columns={[{ label: "Metric" }, { label: "Value", align: "right" }]}
					data={[
						["Sent", toNumberFormat(data.emailPerformance.sent)],
						["Failed", toNumberFormat(data.emailPerformance.failed)],
						["Queued", toNumberFormat(data.emailPerformance.queued)],
						["Delivery rate", formatPercent(data.emailPerformance.deliveryRate)],
						["Access emails", toNumberFormat(data.emailPerformance.productAccessEmails)],
						["Purchase access emails", toNumberFormat(data.emailPerformance.purchasedProductAccessEmails)],
					]}
				/>
			</div>
		</div>
	)
}

type MetricCardProps = {
	Icon: ComponentType<{ className?: string }>
	label: string
	value: string
	detail: string
}
const MetricCard: FC<MetricCardProps> = ({ Icon, label, value, detail }) => (
	<AnalyticsCard className="flex min-w-56 flex-[1_1_220px] flex-col gap-5">
		<AnalyticsCardHeader Icon={Icon} title={label} />
		<div>
			<div className="text-2xl font-bold">{value}</div>
			<div className="mt-1 text-sm text-gray-600">{detail}</div>
		</div>
	</AnalyticsCard>
)

type TableColumn = {
	label: string
	align?: "left" | "right"
	className?: string
}

type DetailTableProps = {
	title: string
	columns: TableColumn[]
	data: ReactNode[][]
	emptyText?: string
	className: string
}
const DetailTable: FC<DetailTableProps> = ({ title, columns, data, emptyText, className }) => (
	<AnalyticsCard className={cn("min-w-0 flex-1", className)}>
		<h2 className="text-sm font-semibold">{title}</h2>
		<div className="mt-5 min-w-0 overflow-hidden">
			<table className="w-full table-fixed">
				<thead>
					<tr className="border-b border-gray-100">
						{columns.map(column => (
							<th
								key={column.label}
								scope="col"
								className={cn(
									"pb-3 text-xs font-semibold text-gray-500",
									getColumnAlignClassName(column),
									column.className,
								)}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				{data.length > 0 ? (
					<tbody>
						{data.map((row, index) => (
							<tr key={index} className="border-b border-gray-100 last:border-b-0">
								{row.map((cell, cellIndex) => (
									<td
										key={cellIndex}
										className={cn(
											"min-w-0 py-3 pr-4 align-middle text-sm last:pr-0",
											getColumnAlignClassName(columns[cellIndex]),
											columns[cellIndex]?.align === "right"
												? "whitespace-nowrap"
												: "wrap-break-word",
										)}
									>
										{cell}
									</td>
								))}
							</tr>
						))}
					</tbody>
				) : (
					<tbody>
						<tr>
							<td colSpan={columns.length} className="py-6 text-center text-sm font-medium text-gray-500">
								{emptyText}
							</td>
						</tr>
					</tbody>
				)}
			</table>
		</div>
	</AnalyticsCard>
)

const FunnelCard: FC<{ data: BusinessAnalytics["signupFunnel"] }> = ({ data }) => {
	const steps = [
		{ label: "Visitors", value: data.visitors },
		{ label: "Signups", value: data.signups },
		{ label: "Buyers", value: data.buyers },
	]
	const maxValue = Math.max(...steps.map(step => step.value ?? 0), 1)

	return (
		<AnalyticsCard className="min-w-[320px] flex-[1_1_360px]">
			<h2 className="text-sm font-semibold">Signup funnel</h2>
			<div className="mt-5 flex flex-col gap-5">
				{steps.map(step => {
					const width =
						step.value === null ? 0 : Math.max((step.value / maxValue) * 100, step.value > 0 ? 3 : 0)
					return (
						<div key={step.label} className="flex flex-col gap-2">
							<div className="flex items-center justify-between gap-3">
								<span className="text-sm font-medium text-gray-700">{step.label}</span>
								<span className="text-sm font-semibold">
									{step.value === null ? "-" : toNumberFormat(step.value)}
								</span>
							</div>
							<div className="h-2 overflow-hidden rounded-full bg-gray-100">
								<div className="bg-primary h-full rounded-full" style={{ width: `${width}%` }} />
							</div>
						</div>
					)
				})}
			</div>
			<div className="mt-5 flex flex-wrap gap-3 text-sm text-gray-600">
				<span>Visitor signup: {formatPercent(data.visitorToSignupRate)}</span>
				<span>Signup purchase: {formatPercent(data.signupToPurchaseRate)}</span>
			</div>
		</AnalyticsCard>
	)
}

const ProductName: FC<{ product: { name: string; type: AnalyticsProductType } }> = ({ product }) => (
	<div className="flex flex-col gap-1">
		<span className="font-medium">{product.name}</span>
		<span className="text-xs text-gray-500">{formatProductType(product.type)}</span>
	</div>
)

const CourseName: FC<{ title: string; pageCount: number }> = ({ title, pageCount }) => (
	<div className="flex flex-col gap-1">
		<span className="font-medium">{title}</span>
		<span className="text-xs text-gray-500">{toNumberFormat(pageCount)} pages</span>
	</div>
)

function getCheckoutRows(checkout: CheckoutAnalytics): ReactNode[][] {
	if (checkout.status === "unavailable") {
		return [
			["Completed", toNumberFormat(checkout.completed)],
			["Abandoned", "-"],
			["Abandonment rate", "-"],
		]
	}

	return [
		["Started", toNumberFormat(checkout.started)],
		["Completed", toNumberFormat(checkout.completed)],
		["Abandoned", toNumberFormat(checkout.abandoned)],
		["Abandonment rate", formatPercent(checkout.abandonmentRate)],
	]
}

function getCheckoutSummary(checkout: CheckoutAnalytics): string {
	if (checkout.status === "unavailable") return `${toNumberFormat(checkout.completed)} completed`
	return `${toNumberFormat(checkout.abandoned)} abandoned from ${toNumberFormat(checkout.started)}`
}

function formatPercent(value: number | null): string {
	if (value === null) return "-"
	if (value > 0 && value < 0.001) return "<0.1%"
	return `${(value * 100).toFixed(1)}%`
}

function formatProductType(type: AnalyticsProductType): string {
	switch (type) {
		case "course":
			return "Course"
		case "video":
			return "Video"
		case "downloadable":
			return "Downloadable"
		case "session":
			return "Session"
		case "event":
			return "Event"
		case "bundle":
			return "Bundle"
		case "membership":
			return "Membership"
	}
}

function getColumnAlignClassName(column: TableColumn | undefined): string {
	return column?.align === "right" ? "text-right" : "text-left"
}

function toRate(numerator: number, denominator: number): number | null {
	return denominator > 0 ? numerator / denominator : null
}

export default BusinessInsights
